import { useState } from 'react';
import type { CalculatorConfig } from '@shared/types';
import type { CalculatorResultaat } from './berekenAdvies';
import type { StapJouwAutoState } from './StapJouwAuto';
import type { StapRijgedragState } from './StapRijgedrag';

interface Props {
  config: CalculatorConfig;
  resultaat: CalculatorResultaat;
  stapAuto: StapJouwAutoState;
  stapRijgedrag: StapRijgedragState;
  onSubmit: (email: string) => void;
}

/**
 * Lead gate shown BEFORE the calculator uitslag.
 * Captures email (required) + voornaam (optional) and subscribes to MailWizz.
 * Calls onSubmit regardless of API success so the uitslag is never blocked.
 */
export default function StapLeadGate({ config, resultaat, stapAuto, stapRijgedrag, onSubmit }: Props) {
  const [email, setEmail] = useState('');
  const [voornaam, setVoornaam] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');

  // Derive calculator data for MailWizz custom fields
  const autoType = stapAuto.categorie ?? (stapAuto.heeftEV ? 'volledig elektrisch' : 'oriënterend');
  const kmPerDag = stapRijgedrag.kmPerDag;
  const laadvermogenAdvies = resultaat.aanbevolenKw;
  const geschikt =
    resultaat.adviesType === 'geschikt'
      ? 'ja'
      : resultaat.adviesType === 'nader_onderzoek'
      ? 'misschien'
      : 'nee';

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email.trim()) return;

    setIsSubmitting(true);
    setError('');

    const body = new FormData();
    body.append('action', 'laadcheck_mailwizz_subscribe');
    body.append('nonce', config.nonce);
    body.append('email', email.trim());
    body.append('voornaam', voornaam.trim());
    body.append('aanbevolen_kw', String(laadvermogenAdvies));
    body.append('auto_type', autoType);
    body.append('km_per_dag', String(kmPerDag));
    body.append('laadvermogen_advies', String(laadvermogenAdvies));
    body.append('geschikt', geschikt);

    try {
      await fetch(config.ajaxUrl, { method: 'POST', body });
      // Persist email + voornaam locally so assessment can pre-fill step 1
      try {
        localStorage.setItem('laadcheck_lead_email', email.trim());
        if (voornaam.trim()) localStorage.setItem('laadcheck_lead_voornaam', voornaam.trim());
      } catch {
        // localStorage unavailable (e.g. strict incognito) — silently ignore
      }
    } catch {
      // Network error: non-fatal, proceed to uitslag
    } finally {
      setIsSubmitting(false);
      onSubmit(email.trim());
    }
  }

  return (
    <div className="lc-lead-gate">

      {/* Eyebrow */}
      <div className="lc-lead-gate__eyebrow" aria-label="Jouw analyse is klaar">
        <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Jouw analyse is klaar
      </div>

      {/* Heading */}
      <h2 className="lc-lead-gate__heading">
        Jouw gratis laadadvies staat <span className="lc-lead-gate__accent">klaar</span>
      </h2>

      <p className="lc-lead-gate__sub">
        Vul je e-mailadres in om jouw persoonlijk laadadvies te bekijken — inclusief aanbevolen laadvermogen en geschatte besparingen.
      </p>

      {/* Blurred preview panel */}
      <div className="lc-lead-gate__preview" aria-hidden="true">
        <div className="lc-lead-gate__preview-label">Jouw resultaten</div>
        <div className="lc-lead-gate__preview-rows">
          <div className="lc-lead-gate__preview-row">
            <div className="lc-lead-gate__preview-icon" />
            <div className="lc-lead-gate__preview-text">
              <div className="lc-lead-gate__preview-bar lc-lead-gate__preview-bar--wide" />
              <div className="lc-lead-gate__preview-bar lc-lead-gate__preview-bar--narrow" />
            </div>
          </div>
          <div className="lc-lead-gate__preview-row">
            <div className="lc-lead-gate__preview-icon" />
            <div className="lc-lead-gate__preview-text">
              <div className="lc-lead-gate__preview-bar lc-lead-gate__preview-bar--medium" />
              <div className="lc-lead-gate__preview-bar lc-lead-gate__preview-bar--narrow" />
            </div>
          </div>
          <div className="lc-lead-gate__preview-row">
            <div className="lc-lead-gate__preview-icon" />
            <div className="lc-lead-gate__preview-text">
              <div className="lc-lead-gate__preview-bar lc-lead-gate__preview-bar--wide" />
              <div className="lc-lead-gate__preview-bar lc-lead-gate__preview-bar--narrow" />
            </div>
          </div>
        </div>
        <div className="lc-lead-gate__lock-overlay">
          <div className="lc-lead-gate__lock-icon">🔒</div>
          <p className="lc-lead-gate__lock-text">Vul je e-mail in om de volledige uitslag te zien</p>
        </div>
      </div>

      {/* Email capture form */}
      <form onSubmit={handleSubmit} className="lc-lead-gate__form" noValidate>
        <div className="lc-lead-gate__field">
          <label htmlFor="lg-voornaam" className="lc-lead-gate__label">
            Voornaam <span className="lc-lead-gate__optional">(optioneel)</span>
          </label>
          <input
            id="lg-voornaam"
            type="text"
            className="lc-input"
            placeholder="Jouw voornaam"
            value={voornaam}
            onChange={e => setVoornaam(e.target.value)}
            autoComplete="given-name"
          />
        </div>

        <div className="lc-lead-gate__field">
          <label htmlFor="lg-email" className="lc-lead-gate__label">
            E-mailadres <span className="lc-lead-gate__required">*</span>
          </label>
          <input
            id="lg-email"
            type="email"
            className="lc-input"
            placeholder="jouw@emailadres.nl"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
            autoComplete="email"
            aria-required="true"
          />
        </div>

        {error && <p className="lc-error" role="alert">{error}</p>}

        <button
          type="submit"
          className="lc-btn lc-btn--cta lc-lead-gate__submit"
          disabled={isSubmitting || !email.trim()}
        >
          {isSubmitting ? 'Even geduld...' : 'Bekijk mijn uitslag →'}
        </button>

        <p className="lc-lead-gate__disclaimer">
          🔒 Gratis &amp; vrijblijvend · Geen spam · Uitschrijven met één klik
        </p>
      </form>

      {/* Trust badges */}
      <div className="lc-lead-gate__trust" aria-label="Vertrouwensindicatoren">
        <span className="lc-lead-gate__trust-item">✓ Gratis rapport</span>
        <span className="lc-lead-gate__trust-item">✓ Direct resultaat</span>
        <span className="lc-lead-gate__trust-item">✓ AVG-conform</span>
      </div>

    </div>
  );
}
